import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import { getLoginRequest } from '../services/msalConfig';
import { ssoLogin } from '../services/apiService';

interface UseSSO {
  isLoading: boolean;
  error: string | null;
  loginWithSSO: () => Promise<void>;
  logout: () => Promise<void>;
  account: AccountInfo | null;
}

export const useSSO = (): UseSSO => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loginWithSSO = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const loginRequest = getLoginRequest();
      
      // Attempt to login with popup
      const response: AuthenticationResult = await instance.loginPopup(loginRequest);
      
      if (response.account && response.idToken) {
        // Send the actual ID token to the server for verification
        // This is much more secure than just sending the username
        const loginResult = await ssoLogin(response.idToken, 'aptean');
        
        if (loginResult.status === 'success') {
          // Store user data (token is now managed by HTTP-only cookies)
          if (loginResult.data?.user) {
            // Add SSO login flag to indicate this was an SSO login
            const userWithSSO = {
              ...loginResult.data.user,
              isSSO: true
            };
            localStorage.setItem('user', JSON.stringify(userWithSSO));
            localStorage.setItem('authMethod', 'sso'); // Track auth method
          }
          
          // Redirect to dashboard or main app
          window.location.href = '/dashboard';
        } else {
          throw new Error(loginResult.message || 'SSO login failed');
        }
      } else {
        throw new Error('No account information or ID token received from Microsoft');
      }
    } catch (error) {
      // Handle specific MSAL errors
      if (error && typeof error === 'object' && 'errorCode' in error) {
        const msalError = error as any;
        switch (msalError.errorCode) {
          case 'popup_window_error':
            setError('Please allow popups and try again');
            break;
          case 'user_cancelled':
            setError('Login was cancelled');
            break;
          case 'network_error':
            setError('Network error. Please check your connection');
            break;
          default:
            setError(msalError.errorMessage || 'Authentication failed');
        }
      } else {
        setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    // Navigate to centralized logout route
    // This provides better UX with loading states and proper cleanup
    navigate('/auth/logout');
  }, [navigate]);

  return {
    isLoading,
    error,
    loginWithSSO,
    logout,
    account: accounts.length > 0 ? accounts[0] : null,
  };
};